import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import employeeQualificationService from '../../../services/EmployeeQualificationService';
import toast from 'react-hot-toast';
import useQualificationUpload from '../helper/useQualificationUpload';

/* ─── Helpers ─────────────────────────────────────────────── */
const isPdfUrl = (url) => {
  // eslint-disable-next-line no-undef
  if (url instanceof File) return url.type === 'application/pdf';
  return typeof url === 'string' && url.toLowerCase().endsWith('.pdf');
};

// eslint-disable-next-line no-undef
const resolveUrl = (url) => (url instanceof File ? URL.createObjectURL(url) : url);

/* ─── Reusable sub-components ─────────────────────────────── */

/** A labelled field row */
const Field = ({ label, value }) =>
  value ? (
    <div className="tw-flex tw-items-start tw-gap-2 tw-text-sm">
      <span className="tw-min-w-[110px] tw-font-medium tw-text-gray-500">{label}</span>
      <span className="tw-text-gray-800">{value}</span>
    </div>
  ) : null;

Field.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any
};

/** Image or PDF preview */
const FilePreview = ({ url, title, onClick, className = '' }) => {
  const fileUrl = resolveUrl(url);
  return isPdfUrl(url) ? (
    <iframe src={fileUrl} width="100%" height="100%" title={title} className={`tw-border-0 ${className}`} />
  ) : (
    <img
      src={fileUrl || '/placeholder.svg'}
      alt="qualification"
      onClick={onClick}
      className={`tw-w-full tw-h-full tw-object-contain ${onClick ? 'tw-cursor-pointer' : ''} ${className}`}
    />
  );
};

FilePreview.propTypes = {
  url: PropTypes.any,
  title: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string
};

/** Pill-badge for status */
const StatusBadge = ({ label, color }) => (
  <span
    className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold ${color}`}
  >
    {label}
  </span>
);

StatusBadge.propTypes = {
  label: PropTypes.string,
  color: PropTypes.string
};

/* ─── Styled button helpers ───────────────────────────────── */
const btnBase =
  'tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-transition-all disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-outline-none';
const btnPrimary = `${btnBase} tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700`;
const btnOutline = `${btnBase} tw-border tw-border-gray-300 tw-text-gray-700 hover:tw-bg-gray-50`;
const btnDanger = `${btnBase} tw-border tw-border-red-300 tw-text-red-600 hover:tw-bg-red-50`;
const btnSuccess = `${btnBase} tw-bg-emerald-600 tw-text-white hover:tw-bg-emerald-700`;
const btnSecondary = `${btnBase} tw-border tw-border-blue-300 tw-text-blue-600 hover:tw-bg-blue-50`;

/* ─── Date input with a label ─────────────────────────────── */
const DateField = ({ label, name, value, onChange }) => (
  <div className="tw-flex tw-flex-col tw-gap-1">
    <label className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500">{label}</label>
    <input
      type="date"
      name={name}
      value={value}
      onChange={onChange}
      className="tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-1.5 tw-text-sm tw-text-gray-800 focus:tw-border-blue-500 focus:tw-outline-none"
    />
  </div>
);

DateField.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func
};

/* ─── Qualification info panel ────────────────────────────── */
const QualInfo = ({ item, title, badge }) => (
  <div className="tw-space-y-2">
    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
      <h4 className="tw-font-semibold tw-text-gray-800 tw-text-sm">{title}</h4>
      {badge}
    </div>
    <Field label="Name" value={item?.name} />
    <Field label="Issue Date" value={item?.issueDate} />
    <Field label="Expiry Date" value={item?.expiryDate} />
    <Field label="Card Number" value={item?.referenceNumber} />
  </div>
);

QualInfo.propTypes = {
  item: PropTypes.any,
  title: PropTypes.string,
  badge: PropTypes.any
};

/* ─── Main Component ──────────────────────────────────────── */
const QualificationPendingItem = ({
  qualificationItem,
  setQualificationRecord,
  employeeId,
  setEmployeesQualifications,
  rows,
  qualificationName,
  changeDialogSizePExisting,
  setChangeDialogSizePExisting,
  changeDialogSizePUpdated,
  setChangeDialogSizePUpdated
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { selectFile, isConverting, fileError } = useQualificationUpload();
  const [editClicked, setEditClicked] = useState(false);

  const [editQualificationItem, setEditedQualificationItem] = useState({
    employeeKeyPayId: qualificationItem[1]?.employeeKeyPayId,
    pendingQualificationId: qualificationItem[1]?._id,
    issueDate: qualificationItem[1]?.issueDate ? format(new Date(qualificationItem[1].issueDate), 'yyyy-MM-dd') : '',
    expiryDate: qualificationItem[1]?.expiryDate ? format(new Date(qualificationItem[1].expiryDate), 'yyyy-MM-dd') : '',
    referenceNumber: qualificationItem[1]?.referenceNumber || '',
    qualificationImage: qualificationItem[1]?.qualificationUrl || ''
  });

  /* ── Handlers ──────────────────────────────────────────── */
  const handleRejectQualification = async (e, qId) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const dbQualification = await employeeQualificationService.getQualificationFromDb(qId);
      const dbQualificationStatus = dbQualification?.data?.data?.status;
      if (dbQualificationStatus !== 'pending') {
        toast.success(`The qualification was already ${dbQualificationStatus}.`);
        return;
      }
      // eslint-disable-next-line no-undef
      const rejectReason = prompt('Reason to reject the qualification', '');
      if (rejectReason !== null && rejectReason !== '') {
        const response = await employeeQualificationService.changeEmployeeQualificationStatus(qId, rejectReason);
        const employeeUpdatedQualifications = await employeeQualificationService.getEmployeeUpdatedQualifications(
          employeeId
        );
        const employeeQualificationRecords = employeeUpdatedQualifications?.data?.data;
        setQualificationRecord(employeeQualificationRecords);
        if (!employeeQualificationRecords.length) {
          setEmployeesQualifications(rows.map((row) => (row.id === qId ? { ...row, status: 'rejected' } : row)));
        }
        toast.success(response?.data?.message || 'Qualification rejected successfully.');
      } else {
        toast.error('Please write a reason to reject the qualification');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error rejecting qualification.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditQualification = (e) => {
    setEditedQualificationItem({ ...editQualificationItem, [e.target.name]: e.target.value });
  };

  const handleUpdatedQualification = () => {
    const qualificationData = {
      employeeKeyPayId: editQualificationItem?.employeeKeyPayId,
      pendingQualificationId: editQualificationItem?.pendingQualificationId,
      issueDate: editQualificationItem?.issueDate,
      expiryDate: editQualificationItem?.expiryDate,
      referenceNumber: editQualificationItem?.referenceNumber
    };
    // eslint-disable-next-line no-undef
    const formData = new FormData();
    formData.append('qualificationData', JSON.stringify(qualificationData));
    // eslint-disable-next-line no-undef
    const userId = localStorage.getItem('userId');
    formData.append('userId', userId);
    // eslint-disable-next-line no-undef
    if (editQualificationItem?.qualificationImage instanceof File) {
      formData.append('avatar', editQualificationItem.qualificationImage);
    }
    employeeQualificationService
      .updateEmployeeQualificationByAdmin(formData)
      .then((data) => {
        if (data?.data?.data?.length) {
          const editingObject = data?.data?.data
            ?.flat()
            ?.find((item) => item._id === editQualificationItem?.pendingQualificationId);
          setEmployeesQualifications(
            rows.map((row) =>
              row.id === qualificationData?.pendingQualificationId
                ? {
                    ...row,
                    qualificationUrl: editingObject?.qualificationUrl || '',
                    issueDate: editingObject?.issueDate ? format(new Date(editingObject.issueDate), 'yyyy-MM-dd') : '',
                    expiryDate: editingObject?.expiryDate
                      ? format(new Date(editingObject.expiryDate), 'yyyy-MM-dd')
                      : '',
                    referenceNumber: editingObject?.referenceNumber
                  }
                : row
            )
          );
          setQualificationRecord((prev) =>
            prev.map((item) =>
              item[1]?._id === editingObject._id
                ? item.map((qual) => (qual?._id === editingObject._id ? { ...qual, ...editingObject } : qual))
                : item
            )
          );
        }
        toast.success(data?.data?.message);
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message?.message || 'Qualification has not updated');
      })
      .finally(() => setEditClicked(false));
  };

  const handleEmployeeQualificationEdit = async (e, qId) => {
    e.preventDefault();
    setEditClicked(true);
    setEditedQualificationItem({ ...editQualificationItem, pendingQualificationId: qId });
  };

  const handleEditQualificationFile = (e) => {
    const name = e.target.name;
    selectFile(e.target.files?.[0], (file) => {
      setEditedQualificationItem((current) => ({ ...current, [name]: file }));
    });
    e.target.value = '';
  };

  const handleAcceptQualification = async (e, qId) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const dbQualification = await employeeQualificationService.getQualificationFromDb(qId);
      const dbQualificationStatus = dbQualification?.data?.data?.status;
      if (dbQualificationStatus !== 'pending') {
        toast.success(`The qualification was already ${dbQualificationStatus}.`);
        return;
      }
      const response = await employeeQualificationService.createEmployeeQualification(qId);
      toast.success(response?.data?.message || 'Qualification accepted successfully.');
      const employeeUpdatedQualifications = await employeeQualificationService.getEmployeeUpdatedQualification(
        employeeId,
        qualificationName
      );
      const employeeQualificationRecords = employeeUpdatedQualifications?.data?.data;
      if (!employeeQualificationRecords.length) {
        setEmployeesQualifications(rows.map((row) => (row.id === qId ? { ...row, status: 'accepted' } : row)));
      }
      setQualificationRecord(employeeQualificationRecords);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error accepting qualification.');
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Derived values ──────────────────────────────────────── */
  const employeeName = qualificationItem[0]?.employeeDisplayName || qualificationItem[1]?.employeeDisplayName;
  const hasBothVersions =
    (qualificationItem.length > 0 && qualificationItem[0] !== null) ||
    (qualificationItem.length > 0 && qualificationItem[0] === null && qualificationItem[1]?.status === 'pending');

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <div className="tw-mb-6">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3 tw-px-1">
        <div className="tw-h-8 tw-w-8 tw-rounded-full tw-bg-blue-100 tw-text-blue-700 tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold tw-select-none">
          {employeeName?.[0]?.toUpperCase() || '?'}
        </div>
        <div>
          <p className="tw-text-xs tw-text-gray-500 tw-leading-none">Qualification update by</p>
          <p className="tw-font-semibold tw-text-gray-800 tw-text-sm">{employeeName}</p>
        </div>
        <StatusBadge label="Pending Review" color="tw-bg-amber-100 tw-text-amber-700 tw-ml-auto" />
      </div>

      {hasBothVersions ? (
        /* ══ Three-column view ══════════════════════════════════════ */
        <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm tw-overflow-hidden">
          {/* ── Normal (non-enlarged) view ── */}
          {!changeDialogSizePExisting && !changeDialogSizePUpdated && (
            <div className="tw-flex tw-flex-row tw-items-stretch tw-divide-x tw-divide-gray-100">
              {/* Existing column */}
              <div className="tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-p-5 tw-gap-3">
                <QualInfo
                  item={qualificationItem[0]}
                  title={`Existing – ${qualificationItem[0]?.name || qualificationItem[1]?.name}`}
                  badge={<StatusBadge label="Current" color="tw-bg-gray-100 tw-text-gray-600" />}
                />
                <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-400">Document</p>
                <div
                  className="tw-h-64 tw-w-full tw-rounded-lg tw-bg-gray-50 tw-border tw-border-gray-100 tw-overflow-hidden tw-cursor-pointer hover:tw-border-gray-300 tw-transition-colors"
                  onClick={() => setChangeDialogSizePExisting(true)}
                  title="Click to enlarge"
                >
                  <FilePreview
                    url={qualificationItem[0]?.qualificationUrl}
                    title="Existing Qualification"
                    onClick={() => setChangeDialogSizePExisting(true)}
                  />
                </div>
                {!isPdfUrl(qualificationItem[0]?.qualificationUrl) && (
                  <p className="tw-text-xs tw-text-gray-400 tw-text-center">🔍 Click image to enlarge</p>
                )}
              </div>

              {/* Updated column */}
              <div className="tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-p-5 tw-gap-3">
                <QualInfo
                  item={qualificationItem[1]}
                  title={`Updated – ${qualificationItem[1]?.name}`}
                  badge={<StatusBadge label="Submitted" color="tw-bg-blue-100 tw-text-blue-700" />}
                />

                {editClicked ? (
                  <div className="tw-space-y-3 tw-bg-blue-50 tw-rounded-lg tw-p-4 tw-border tw-border-blue-100">
                    <p className="tw-text-xs tw-font-semibold tw-text-blue-700 tw-uppercase tw-tracking-wide">
                      Edit Fields
                    </p>
                    {qualificationItem[1]?.issueDate && (
                      <DateField
                        label="Issue Date"
                        name="issueDate"
                        value={format(new Date(editQualificationItem.issueDate), 'yyyy-MM-dd')}
                        onChange={handleEditQualification}
                      />
                    )}
                    {qualificationItem[1]?.expiryDate && (
                      <DateField
                        label="Expiry Date"
                        name="expiryDate"
                        value={format(new Date(editQualificationItem.expiryDate), 'yyyy-MM-dd')}
                        onChange={handleEditQualification}
                      />
                    )}
                    {qualificationItem[1]?.referenceNumber && (
                      <div className="tw-flex tw-flex-col tw-gap-1">
                        <label className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500">
                          Card Number
                        </label>
                        <input
                          name="referenceNumber"
                          value={editQualificationItem.referenceNumber}
                          onChange={handleEditQualification}
                          placeholder="Card number"
                          className="tw-rounded-md tw-border tw-border-gray-300 tw-px-3 tw-py-1.5 tw-text-sm tw-text-gray-800 focus:tw-border-blue-500 focus:tw-outline-none"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-400">
                      Document
                    </p>
                    <div
                      className="tw-h-64 tw-w-full tw-rounded-lg tw-bg-gray-50 tw-border tw-border-gray-100 tw-overflow-hidden tw-cursor-pointer hover:tw-border-gray-300 tw-transition-colors"
                      onClick={() => setChangeDialogSizePUpdated(true)}
                      title="Click to enlarge"
                    >
                      <FilePreview
                        url={qualificationItem[1]?.qualificationUrl}
                        title="Updated Qualification"
                        onClick={() => setChangeDialogSizePUpdated(true)}
                      />
                    </div>
                    {!isPdfUrl(qualificationItem[1]?.qualificationUrl) && (
                      <p className="tw-text-xs tw-text-gray-400 tw-text-center">🔍 Click image to enlarge</p>
                    )}
                  </>
                )}
              </div>

              {/* Actions column */}
              <div className="tw-w-44 tw-shrink-0 tw-p-5 tw-flex tw-flex-col tw-gap-3 tw-bg-gray-50">
                <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-400 tw-mb-1">
                  Actions
                </p>
                {editClicked ? (
                  <>
                    <label className="tw-block">
                      <span className={btnSecondary + ' tw-w-full tw-cursor-pointer'}>📎 Choose File</span>
                      <input
                        type="file"
                        accept="image/*,.heic,.heif,.pdf"
                        id="qualificationImage"
                        name="qualificationImage"
                        className="tw-sr-only"
                        onChange={handleEditQualificationFile}
                      />
                    </label>
                    {editQualificationItem.qualificationImage &&
                      typeof editQualificationItem.qualificationImage !== 'string' && (
                        <p className="tw-text-xs tw-text-gray-500 tw-truncate">
                          📄 {editQualificationItem.qualificationImage.name}
                        </p>
                      )}
                    {fileError && <p role="alert">{fileError}</p>}
                    <button
                      onClick={handleUpdatedQualification}
                      disabled={isLoading || isConverting || Boolean(fileError)}
                      className={btnPrimary + ' tw-w-full'}
                    >
                      {isConverting ? 'Converting photo…' : isLoading ? '…' : '✓ Update'}
                    </button>
                    <button
                      onClick={() => setEditClicked(false)}
                      disabled={isLoading}
                      className={btnOutline + ' tw-w-full'}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={(e) => handleEmployeeQualificationEdit(e, qualificationItem[1]?._id)}
                      disabled={isLoading}
                      className={btnSecondary + ' tw-w-full'}
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => handleAcceptQualification(e, qualificationItem[1]?._id)}
                      disabled={isLoading}
                      className={btnSuccess + ' tw-w-full'}
                    >
                      {isLoading ? '…' : 'Accept'}
                    </button>
                    <button
                      onClick={(e) => handleRejectQualification(e, qualificationItem[1]?._id)}
                      disabled={isLoading}
                      className={btnDanger + ' tw-w-full'}
                    >
                      {isLoading ? '…' : 'Reject'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Enlarged existing image ── */}
          {changeDialogSizePExisting && (
            <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-6 tw-p-6">
              <div className="md:tw-w-64 tw-shrink-0 tw-space-y-2">
                <QualInfo
                  item={qualificationItem[0]}
                  title={`Existing – ${qualificationItem[0]?.name || qualificationItem[1]?.name}`}
                />
              </div>
              <div
                className="tw-flex-1 tw-rounded-xl tw-bg-gray-50 tw-border tw-border-gray-200 tw-overflow-hidden tw-cursor-pointer"
                style={{ minHeight: '400px' }}
                onClick={() => setChangeDialogSizePExisting(false)}
                title="Click to go back"
              >
                <FilePreview url={qualificationItem[0]?.qualificationUrl} title="Existing Qualification Enlarged" />
              </div>
              <button onClick={() => setChangeDialogSizePExisting(false)} className={btnOutline + ' tw-self-start'}>
                ← Back
              </button>
            </div>
          )}

          {/* ── Enlarged updated image ── */}
          {changeDialogSizePUpdated && (
            <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-6 tw-p-6">
              <div className="md:tw-w-64 tw-shrink-0 tw-space-y-2">
                <QualInfo item={qualificationItem[1]} title={`Updated – ${qualificationItem[1]?.name}`} />
              </div>
              <div
                className="tw-flex-1 tw-rounded-xl tw-bg-gray-50 tw-border tw-border-gray-200 tw-overflow-hidden tw-cursor-pointer"
                style={{ minHeight: '400px' }}
                onClick={() => setChangeDialogSizePUpdated(false)}
                title="Click to go back"
              >
                <FilePreview url={qualificationItem[1]?.qualificationUrl} title="Updated Qualification Enlarged" />
              </div>
              <button onClick={() => setChangeDialogSizePUpdated(false)} className={btnOutline + ' tw-self-start'}>
                ← Back
              </button>
            </div>
          )}
        </div>
      ) : (
        /* ══ Single-panel (new qualification, no existing) view ══════ */
        <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm tw-overflow-hidden">
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-divide-y md:tw-divide-y-0 md:tw-divide-x tw-divide-gray-100">
            {/* Info */}
            <div className="tw-flex-1 tw-p-5 tw-space-y-2 tw-flex tw-flex-col tw-justify-center">
              <h4 className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-mb-2">{qualificationItem[1]?.name}</h4>
              <Field label="Issue Date" value={qualificationItem[1]?.issueDate} />
              <Field label="Expiry Date" value={qualificationItem[1]?.expiryDate} />
              <Field label="Card Number" value={qualificationItem[1]?.referenceNumber} />
            </div>
            {/* Document */}
            <div className="tw-flex-1 tw-p-5">
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-400 tw-mb-2">
                Document
              </p>
              <div className="tw-h-56 tw-w-full tw-rounded-lg tw-bg-gray-50 tw-border tw-border-gray-100 tw-overflow-hidden">
                <FilePreview
                  url={qualificationItem[1]?.qualificationUrl}
                  title="Qualification"
                  onClick={() => setChangeDialogSizePUpdated((p) => !p)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QualificationPendingItem;

QualificationPendingItem.propTypes = {
  qualificationItem: PropTypes.any,
  setQualificationRecord: PropTypes.any,
  employeeId: PropTypes.any,
  setEmployeesQualifications: PropTypes.func,
  rows: PropTypes.any,
  qualificationName: PropTypes.string,
  changeDialogSizePExisting: PropTypes.bool,
  setChangeDialogSizePExisting: PropTypes.func,
  changeDialogSizePUpdated: PropTypes.bool,
  setChangeDialogSizePUpdated: PropTypes.func
};
